import { Download, Lock, MailWarning, Paperclip, Phone, ShieldCheck } from 'lucide-react';

import { formatInstant } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/ui/empty-state';
import {
  DELIVERY_STATUS_LABELS,
  type ConversationDetail,
  type ConversationMessageView,
} from '@shared/schemas/conversation';
import { MessageDetails } from './MessageDetails';
import { MessageHtml } from './MessageHtml';

/*
 * One conversation, read the way people read conversations — ROADMAP.md §9.3.
 *
 * **Rewritten 2026-08-08 on the owner's finding.** In his words: *"the way the
 * messages are threaded ... right now it seems more like a ticketing system
 * than a conversational tool"*, and what he wanted was something closer to a
 * text-message thread on a phone.
 *
 * He was right, and the reason is worth writing down rather than just fixing.
 * The first version put every message in an identical full-width bordered card
 * with a header row on it — sender, timestamp, provenance chip — and a delivery
 * line underneath. Every one of those pieces is *correct*, and together they
 * meant a customer's one-line question and the office's one-line answer looked
 * like two records in a queue rather than two people talking.
 *
 * **The fix is not fewer facts, it is putting them where a reader wants them.**
 * Four things changed:
 *
 *   1. **Sides.** The customer's messages sit left, ours sit right. That single
 *      cue does most of the work a header row was doing, and it does it without
 *      being read.
 *   2. **Runs.** Consecutive messages from the same side group together: one
 *      name at the top, one time at the bottom. A person sending three lines in
 *      a row is one turn in a conversation, not three tickets.
 *   3. **Day dividers**, so a thread spanning weeks reads as one.
 *   4. **The email machinery moved behind a Details link** — the addresses, the
 *      provenance, the delivery status, and now the raw headers. That is the
 *      other half of what he asked for: *"useful for checking the headers for
 *      anything malicious"*.
 *
 * **What deliberately did not change is the thing that must not.** An internal
 * note and a logged phone call are still full-width, still visibly not
 * messages, still labelled in words. They are not bubbles and they take no
 * side, because the cost of somebody reading a note as a sent reply is a
 * customer never getting an answer they think they were promised.
 *
 * **A component rather than a page**, because 9.7 puts this same thread on the
 * client record and in the leads pane. One thread renderer means the customer's
 * history reads identically wherever it is opened.
 */

/** Bytes, for a person. `24 KB`, not `24576`. */
function fileSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The time on its own — the day is already on the divider above. */
function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * The calendar day, as the reader's own browser reckons it.
 *
 * **Not `todayInBusinessZone`**, and the difference is deliberate. That helper
 * exists because a *job* at 9am Tuesday is a wall-clock fact about Cedar City.
 * A message arrived at an instant, and the question a divider answers is "was
 * this today, for me, reading it now" — so the reader's zone is the right one.
 */
function dayKeyOf(iso: string): string {
  return new Date(iso).toDateString();
}

function dayLabelOf(iso: string): string {
  const day = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (day.toDateString() === today.toDateString()) return 'Today';
  if (day.toDateString() === yesterday.toDateString()) return 'Yesterday';

  return day.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    // Only when it is not this year — "12 March 2025" on a thread from last
    // week is noise, and "12 March" on one from two years ago is a lie.
    ...(day.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** True when these two are one turn in the conversation rather than two. */
function sameRun(a: ConversationMessageView, b: ConversationMessageView): boolean {
  if (a.direction !== b.direction) return false;
  // Notes and calls never group — each is its own deliberate act by a person,
  // and stacking two would blur whose remark is whose.
  if (a.direction === 'note' || a.direction === 'phone') return false;
  if (dayKeyOf(a.createdAt) !== dayKeyOf(b.createdAt)) return false;
  // An outbound run is only a run if the same admin wrote both.
  if (a.authorName !== b.authorName) return false;

  const gap = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  // Five minutes. Long enough that somebody typing a follow-up thought stays in
  // one turn; short enough that this morning and this afternoon do not merge.
  return gap >= 0 && gap < 5 * 60 * 1000;
}

function DayDivider({ iso }: { iso: string }) {
  return (
    <li className="flex items-center gap-3 py-2" aria-hidden="false">
      <span className="bg-ink-200 h-px flex-1" />
      <span className="text-caption text-ink-500">{dayLabelOf(iso)}</span>
      <span className="bg-ink-200 h-px flex-1" />
    </li>
  );
}

function Attachments({ message }: { message: ConversationMessageView }) {
  if (message.attachments.length === 0) return null;

  return (
    <ul className="mt-2 flex flex-col gap-1.5">
      {message.attachments.map((attachment) => (
        <li key={attachment.id}>
          {/*
            **A download, always.** `download` plus the server's
            `Content-Disposition` and neutral content type — nothing a stranger
            attached is ever opened in place (business-rules.md §10).
          */}
          <a
            href={attachment.downloadPath}
            download={attachment.fileName}
            className="border-ink-200 hover:border-bark-300 flex min-h-11 items-center gap-2 rounded-sm border bg-white px-3 py-2 text-meta text-bark-900"
          >
            <Paperclip aria-hidden="true" className="size-4 shrink-0 text-ink-500" />
            <span className="truncate">{attachment.fileName}</span>
            <span className="text-caption ml-auto shrink-0 text-ink-500">
              {fileSize(attachment.sizeBytes)}
            </span>
            <Download aria-hidden="true" className="size-4 shrink-0 text-ink-500" />
          </a>
        </li>
      ))}
    </ul>
  );
}

/**
 * The time and fate of a message, shown under the last bubble of a run.
 *
 * **Time first, then only what is worth interrupting for.** A successful send
 * says "Sent" and stops; a failure says so in the receiving server's own words
 * and in the alarm colour, because that is the one thing on a thread somebody
 * has to act on. A verified inbound reply gets a quiet shield — the rest of the
 * provenance lives behind Details.
 *
 * **Returned as fragments rather than a row**, because `MessageDetails` owns
 * the row: the Details link is on every message and this is on the last of a
 * run, and two separate rows under one bubble is the layout this replaced.
 */
function MetaSpans({ message, mine }: { message: ConversationMessageView; mine: boolean }) {
  const failed = message.deliveryStatus === 'failed' || message.deliveryStatus === 'bounced';
  const verified = message.direction === 'inbound' && message.matchMethod === 'token';

  return (
    <>
      <span className="text-caption text-ink-500">{timeOf(message.createdAt)}</span>

      {verified ? (
        <span
          className="text-caption inline-flex items-center gap-1 text-ink-500"
          title="This reply came back to the private address used for this conversation."
        >
          <ShieldCheck aria-hidden="true" className="size-3" />
          Verified
        </span>
      ) : null}

      {mine ? (
        <span
          className={cn(
            'text-caption inline-flex items-center gap-1',
            failed ? 'text-status-priority-text' : 'text-ink-500',
          )}
        >
          {failed ? <MailWarning aria-hidden="true" className="size-3" /> : null}
          {DELIVERY_STATUS_LABELS[message.deliveryStatus]}
          {/* The receiving server's own words. The office can act on "mailbox
              full" in a way they cannot act on "failed". */}
          {failed && message.deliveryDetail !== null ? ` — ${message.deliveryDetail}` : ''}
        </span>
      ) : null}
    </>
  );
}

/**
 * One bubble.
 *
 * **Notes and phone calls are not bubbles** — see the file header. They keep
 * the full width, the label and the colour they had, because telling them apart
 * from a sent message at a glance is worth more than visual consistency.
 */
function Message({
  conversationId,
  message,
  startsRun,
  endsRun,
}: {
  conversationId: string;
  message: ConversationMessageView;
  startsRun: boolean;
  endsRun: boolean;
}) {
  if (message.direction === 'phone') {
    return (
      <li className="border-ink-200 border-l-ink-400 rounded-lg border border-l-4 bg-ink-50 p-3">
        <p className="text-caption flex items-center gap-1.5 font-medium uppercase tracking-wide text-ink-700">
          <Phone aria-hidden="true" className="size-3.5" />
          Phone call — logged by the office, not sent
        </p>
        <p className="mt-1.5 whitespace-pre-wrap text-body text-ink-900">{message.bodyText}</p>
        <p className="text-caption mt-1.5 text-ink-500">
          {message.authorName ?? 'Someone'} · {formatInstant(message.createdAt)}
        </p>
      </li>
    );
  }

  if (message.direction === 'note') {
    return (
      <li className="rounded-lg border border-l-4 border-honey-300 border-l-honey-500 bg-honey-50/60 p-3">
        <p className="text-caption flex items-center gap-1.5 font-medium uppercase tracking-wide text-bark-700">
          <Lock aria-hidden="true" className="size-3.5" />
          Internal note — not sent to the customer
        </p>
        <p className="mt-1.5 whitespace-pre-wrap text-body text-ink-900">{message.bodyText}</p>
        <p className="text-caption mt-1.5 text-ink-500">
          {message.authorName ?? 'Someone'} · {formatInstant(message.createdAt)}
        </p>
      </li>
    );
  }

  const mine = message.direction === 'outbound';

  return (
    <li className={cn('flex flex-col', mine ? 'items-end' : 'items-start')}>
      {/* One name per turn, at the top of it. */}
      {startsRun ? (
        <span className="text-caption mb-1 px-1 text-ink-500">
          {mine ? (message.authorName ?? 'Honeycomb') : (message.fromName ?? message.fromAddress)}
        </span>
      ) : null}

      <div
        className={cn(
          /*
           * **A bubble, capped at most of the column rather than all of it.**
           * The unused strip on the other side is what makes the side readable
           * at a glance; a full-width block on both sides is the ticket queue
           * this replaced.
           */
          'max-w-[85%] rounded-2xl px-3.5 py-2.5 sm:max-w-[75%]',
          mine ? 'bg-honey-100 text-ink-900' : 'border-ink-200 border bg-white text-ink-900',
          // The corner nearest the speaker squares off on the last of a run —
          // the same tail every messaging app uses to close a turn.
          mine && endsRun ? 'rounded-br-md' : '',
          !mine && endsRun ? 'rounded-bl-md' : '',
        )}
      >
        {/* Only where it changed — otherwise a thread is a column of "Re:". */}
        {message.subject === null ? null : (
          <p className="mb-1 text-meta font-medium text-bark-900">{message.subject}</p>
        )}

        {/*
          **Plain text is the default render** (business-rules.md §10). The HTML
          is shown underneath only as the richer copy — never instead of the
          words.
        */}
        {message.bodyText === null ? null : (
          <p className="whitespace-pre-wrap text-body">{message.bodyText}</p>
        )}

        {message.bodyHtml === null ? null : (
          <MessageHtml html={message.bodyHtml} hasBlockedImages={message.hasBlockedImages} />
        )}

        <Attachments message={message} />
      </div>

      {/*
        **A Details link on every message, a time on the last of a run.** The
        owner asked for the details of *"the individual messages"*, so a link
        that appeared only at the end of a turn would leave the middle of
        somebody's three-line question with no way to check where it came from.
      */}
      <MessageDetails
        conversationId={conversationId}
        message={message}
        mine={mine}
        meta={endsRun ? <MetaSpans message={message} mine={mine} /> : undefined}
      />
    </li>
  );
}

export function ConversationThread({ detail }: { detail: ConversationDetail }) {
  if (detail.messages.length === 0) {
    return (
      <EmptyState
        title="Nothing in this conversation yet"
        hint="When you send a message or the customer replies, it appears here."
      />
    );
  }

  const messages = detail.messages;

  return (
    <ol className="flex flex-col gap-1">
      {messages.map((message, index) => {
        const previous = index === 0 ? null : messages[index - 1]!;
        const next = index === messages.length - 1 ? null : messages[index + 1]!;

        const newDay =
          previous === null || dayKeyOf(previous.createdAt) !== dayKeyOf(message.createdAt);
        const startsRun = previous === null || newDay || !sameRun(previous, message);
        const endsRun = next === null || !sameRun(message, next);

        return (
          <MessageGroup
            key={message.id}
            conversationId={detail.id}
            message={message}
            newDay={newDay}
            startsRun={startsRun}
            endsRun={endsRun}
          />
        );
      })}
    </ol>
  );
}

/**
 * A divider plus its message, as one key.
 *
 * A fragment rather than a wrapper element, so the `<ol>` keeps `<li>` as its
 * only child type — a `<div>` between list items is invalid and screen readers
 * report the count wrongly.
 */
function MessageGroup({
  conversationId,
  message,
  newDay,
  startsRun,
  endsRun,
}: {
  conversationId: string;
  message: ConversationMessageView;
  newDay: boolean;
  startsRun: boolean;
  endsRun: boolean;
}) {
  return (
    <>
      {newDay ? <DayDivider iso={message.createdAt} /> : null}
      <Message
        conversationId={conversationId}
        message={message}
        startsRun={startsRun}
        endsRun={endsRun}
      />
      {/* Space between turns, not between lines of one turn. */}
      {endsRun ? <li aria-hidden="true" className="h-2" /> : null}
    </>
  );
}

/**
 * The thread's history — every send, receipt, delivery, status change and
 * assignment, with who and when.
 *
 * **Shown to the office**, which is why `conversation_event` exists separately
 * from `audit_log` (data-model.md §8a). An entry with no actor was the mail
 * system, and it says so rather than leaving a blank where a name goes.
 */
export function ConversationHistory({ detail }: { detail: ConversationDetail }) {
  if (detail.events.length === 0) return null;

  return (
    <ol className="flex flex-col gap-2">
      {detail.events.map((event) => (
        <li key={event.id} className="text-ink-600 flex flex-wrap items-baseline gap-x-2 text-meta">
          <span className="text-ink-900">{event.description}</span>
          <span className="text-caption text-ink-500">
            {event.actorName ?? 'Mail system'} · {formatInstant(event.createdAt)}
          </span>
        </li>
      ))}
    </ol>
  );
}
